<template>
  <registration-layout hide-nav class="account-registration-usher" size="tiny">
    <loading-placeholder v-if="isLoading" :lines="10" />
    <!-- ACTION COMPLETED STATE: Expired trial extension requested -->
    <template v-else-if="expiredTrialExtensionRequested">
      <h3>We're working on it! 📩</h3>
      <p>
        We'll send you an email once we've extended your trial. You can expect to hear from us 
        Monday through Friday between 9am and 5pm U.S. Central Time.
      </p>
    </template>
    <!-- Unregistered -->
    <template v-else-if="unregistered">
      <h3>Complete your registration!</h3>
      <template v-if="belongsToActiveAccount">
        <p v-if="belongsToExactlyOneActiveAccount">
          It looks like you've been invited to a DoneDone account for 
          <strong>{{ firstActiveAccountName }}</strong>.
          Do you want to join it?
        </p>
        <p v-else-if="belongsToMultipleActiveAccounts">
          It looks like you've already been invited to 
          <strong>{{existingMembershipInfo.listActiveAccountMembershipNames.length}} accounts</strong> already.
          Instead of creating a new account, do you want to join these instead?
        </p>
        <div class="call-to-action">
          <dd-button
            color="blue"
            @click="RouteAssistant.goToUrl(existingMembershipInfo.userRegistrationLink)">
            Yes, join
            <template v-if="belongsToExactlyOneActiveAccount">
              this account
            </template>
            <template v-else>
              these accounts
            </template>
          </dd-button>
          <dd-button
            type="flat"
            @click="RouteAssistant.goToAccountRegistrationComplete($router, ddToken, gAuthToken, microsoftToken, gl)"
          >No, create a brand new account</dd-button>
        </div>
      </template>
      <!-- 
        Belongs to no accounts (but is the unregistered owner of a suspended account, otherwise they
        would have been ushered to account registration completion from getExistingMembershipInfo())
      -->
      <template v-else>
        <p>
          It looks like your account is suspended.
          Just complete your registration and enter a credit card to get going. 💳
        </p>
        <div class="call-to-action">
          <dd-button
            color="blue"
            @click="RouteAssistant.goToUrl(existingMembershipInfo.userRegistrationLink)">
            Complete your user registration
          </dd-button>
        </div>
      </template>
    </template>
    <!-- Registered, owner of suspended accounts -->
    <template v-else-if="ownsSuspendedAccounts">
      <!-- ...specifically, owner of an expired trial -->
      <template v-if="existingMembershipInfo.isOwnerOfExpiredTrial">
        <template v-if="existingMembershipInfo.isOwnerOfOnlyOldExpiredTrials">
          <h3>Your trial has expired ⌛</h3>
          <p>
            It looks like you have a trial that expired a while ago.
            Just sign in and enter a credit card to get going. 💳
          </p>
          <div class="call-to-action">
            <dd-button color="blue" @click="RouteAssistant.goToLogin()">Sign in and reactivate my account</dd-button>
            <dd-button :is-loading="cancellingOldExpiredTrials" type="flat" @click="cancelOldExpiredTrialAndSignupAgain()">Let me start over with a brand new trial!</dd-button>
          </div>
        </template>
        <template v-else-if="existingMembershipInfo.isOwnerOfRecentExpiredTrialWithHeavyUsage">
          <h3>Your trial has expired ⌛</h3>
          <p>
            It looks like you have a trial that's expired.
            Just sign in and enter a credit card to get going. 💳
          </p>
          <div class="call-to-action">
            <dd-button color="blue" @click="RouteAssistant.goToLogin()">Sign in and reactivate my account</dd-button>
          </div>
        </template>
        <template v-else>
          <h3>Your trial has expired ⌛</h3>
          <p>
            It looks like you have a trial that's expired. Would you like a little more time?
          </p>
          <div class="call-to-action">
            <dd-button :is-loading="requestingTrialExtension" color="blue" @click="requestTrialExtension()">Yes, please extend my trial</dd-button>
          </div>
        </template>
      </template>
      <!-- ...OR specifically, owner of a paid, suspended account  -->
      <template v-else-if="existingMembershipInfo.isOwnerOfPaidSuspendedAccount">
        <h3>Reactivate your account</h3>
        <p>
          It looks like your account is suspended. Just sign in and enter a credit card to get going. 💳
        </p>
        <div class="call-to-action">
          <dd-button color="blue" @click="RouteAssistant.goToLogin()">Sign in and reactivate my account</dd-button>
        </div>
      </template>
      <!-- ...and also is a non-owner member of an account -->
      <template v-if="existingMembershipInfo.hasNonOwnerMembershipToActiveAccount">
        <p><br></p>
        <p class="small">
          You also belong to 
          <template v-if="existingMembershipInfo.nonOwnerMembershipToActiveAccountCount === 1">
            an active account.
            <a :href="RouteAssistant.getLoginUrl()" class="just-a-link">Click here</a> to sign in.
          </template>
          <template v-else>
            {{ existingMembershipInfo.nonOwnerMembershipToActiveAccountCount }} active accounts.
            <a :href="RouteAssistant.getLoginUrl()" class="just-a-link">Click here</a> to sign in to them.
          </template>
        </p>
      </template>
    </template>
    <!-- Registered owner of an active trial -->
    <template v-else-if="existingMembershipInfo.isOwnerOfActiveTrial">
      <h3>You already have a trial account 🙌</h3>
      <p>
        You have until <strong>{{ existingMembershipInfo.activeTrialEndingOn }}</strong> to evaluate DoneDone
        for free. After that, you'll need to enter a credit card to continue.
      </p>
      <p><br></p>
      <p>
        If you need any help or need more time with your trial,
        just <a href="https://www.donedone.com/help-center" target="_blank" class="just-a-link">reach out</a> to us!
      </p>
      <div class="call-to-action">
        <dd-button color="blue" @click="RouteAssistant.goToLogin()">Sign in to my trial account</dd-button>
      </div>
      <!-- ...and also is a non-owner member of an account -->
      <template v-if="existingMembershipInfo.hasNonOwnerMembershipToActiveAccount">
        <p><br></p>
        <p class="small">
          You also belong to 
          <template v-if="existingMembershipInfo.nonOwnerMembershipToActiveAccountCount === 1">
            an active account.
            <a :href="RouteAssistant.getLoginUrl()" class="just-a-link">Click here</a> to sign in.
          </template>
          <template v-else>
            {{ existingMembershipInfo.nonOwnerMembershipToActiveAccountCount }} active accounts.
            <a :href="RouteAssistant.getLoginUrl()" class="just-a-link">Click here</a> to sign in to them.
          </template>
        </p>
      </template>
    </template>
    <!-- Registered and belongs to active accounts -->
    <template v-else>
      <template v-if="belongsToExactlyOneActiveAccount">
        <h3>Sign into your account</h3>
        <p>
          It looks like you've already registered and belong to <strong>{{ firstActiveAccountName }}</strong>.
          Instead of creating a new account, do you want to sign in to this one instead?
        </p>
        <div class="call-to-action">
          <dd-button color="blue" @click="RouteAssistant.goToLogin()">Yes, sign in to my existing account</dd-button>
          <dd-button type="flat" @click="RouteAssistant.goToAccountRegistrationComplete($router, ddToken, gAuthToken, microsoftToken, gl)">No, create a brand new account</dd-button>
        </div>
      </template>
      <template v-else>
        <h3>Sign into your accounts</h3>
        <p>
          It looks like you already belong to {{existingMembershipInfo.listActiveAccountMembershipNames.length}} DoneDone accounts.
          Instead of creating a new account, do you want to sign in to these instead?
        </p>
        <div class="call-to-action">
          <dd-button color="blue" @click="RouteAssistant.goToLogin()">Yes, sign in to my existing accounts</dd-button>
          <dd-button type="flat" @click="RouteAssistant.goToAccountRegistrationComplete($router, ddToken, gAuthToken, microsoftToken, gl)">No, create a brand new account</dd-button>
        </div>
      </template>
    </template>
  </registration-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import RegistrationLayout from '@/layouts/registration-layout.vue'
import MainContainer from '@/layouts/main-container.vue'

import doneDoneLogoSrc from '@/assets/img/logos/logo-donedone.svg'
import heartImgSrc from '@/assets/img/heart.svg'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { IRegistrationOutputExistingMembershipInfo } from '@/api/types/response.types'

/* API */
import RegistrationAPI from '@/api/modules/registration.api'

export default defineComponent({
  name: 'AccountRegistrationUsher',

  components: {
    RegistrationLayout,
    MainContainer,
  },

  data() {
    return {
      doneDoneLogo: {
        src: doneDoneLogoSrc,
        alt: 'DoneDone logo',
      },
      heartImg: {
        src: heartImgSrc,
        alt: 'heart',
      },
      RouteAssistant,
      isLoading: true,
      requestingTrialExtension: false,
      cancellingOldExpiredTrials: false,
      expiredTrialExtensionRequested: false,
      tokenErrorMessage: '',
      existingMembershipInfo: {
        listActiveAccountMembershipNames: [] as string[],
      } as IRegistrationOutputExistingMembershipInfo,
    }
  },

  head(): any {
    return {
      title: 'Account registration',
    }
  },

  computed: {
    unregistered(): boolean {
      return !this.existingMembershipInfo.hasUserRegistered
    },
    autoUsherToAccountComplete(): boolean {
      const noOwnedSuspendedAccountsNorActiveMemberships =
        !this.existingMembershipInfo.hasMembershipToActiveAccount &&
        !this.ownsSuspendedAccounts

      return noOwnedSuspendedAccountsNorActiveMemberships
    },
    ownsSuspendedAccounts(): boolean {
      return (
        this.existingMembershipInfo.isOwnerOfExpiredTrial ||
        this.existingMembershipInfo.isOwnerOfPaidSuspendedAccount
      )
    },
    belongsToActiveAccount(): boolean {
      return (
        this.existingMembershipInfo.listActiveAccountMembershipNames.length > 0
      )
    },
    belongsToExactlyOneActiveAccount(): boolean {
      return (
        this.existingMembershipInfo.listActiveAccountMembershipNames.length ===
        1
      )
    },
    belongsToMultipleActiveAccounts(): boolean {
      return (
        this.existingMembershipInfo.listActiveAccountMembershipNames.length > 1
      )
    },
    firstActiveAccountName(): string {
      return this.existingMembershipInfo.listActiveAccountMembershipNames[0]
    },
    hasTokenErrorMessage(): boolean {
      return (
        this.tokenErrorMessage !== undefined &&
        this.tokenErrorMessage.length > 0
      )
    },
    ddToken(): string {
      return TextAssistant.urlTokenCleaner(this.$route.query.donedone_token as string)
    },
    gAuthToken(): string {
      return TextAssistant.urlTokenCleaner(this.$route.query.gauth_token as string)
    },
    microsoftToken(): string {
      return TextAssistant.urlTokenCleaner(this.$route.query.microsoft_token as string)
    },
    gl(): string {
      return this.$route.query._gl as string
    },
  },

  async created() {
    this.isLoading = true
    await this.getExistingMembershipInfo()
    this.isLoading = false
  },

  methods: {
    async requestTrialExtension() {
      try {
        this.requestingTrialExtension = true
        await RegistrationAPI.get_ExpiredTrialExtension(
          this.ddToken,
          this.gAuthToken,
          this.microsoftToken,
        )
        this.expiredTrialExtensionRequested = true
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.requestingTrialExtension = false
      }
    },
    async cancelOldExpiredTrialAndSignupAgain() {
      try {
        this.cancellingOldExpiredTrials = true
        await RegistrationAPI.post_OldExpiredTrialCancellation({
          donedoneToken: this.ddToken,
          gauthToken: this.gAuthToken,
        })
      } finally {
        // Call membership info again and it should usher to account complete...
        await this.getExistingMembershipInfo()
        this.cancellingOldExpiredTrials = false
      }
    },
    async getExistingMembershipInfo() {
      try {
        this.existingMembershipInfo = await RegistrationAPI.get_ExistingMembershipInfo(
          this.ddToken,
          this.gAuthToken,
          this.microsoftToken,
        )
      } catch (error) {
        // For any errors, just pass this along to the account complete for handling
        // (e.g. bad / expired token will be error-handled on account complete GET,
        // No user exists on previous call will become a normal new signup)
      } finally {
        if (this.autoUsherToAccountComplete) {
          // Go to complete....
          RouteAssistant.goToAccountRegistrationComplete(
            this.$router,
            this.ddToken,
            this.gAuthToken,
            this.microsoftToken,
            this.gl,
          )
        }
      }
    },
  },
})
</script>

<style lang="scss">
.account-registration-usher {
  h3 {
    text-align: center;
  }

  .call-to-action {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding-top: 10px;

    > span {
      font-size: 18px;
      color: var(--color-text-third);
    }

    > .dd-button {
      margin-top: 10px;
    }
  }
}
</style>
